import { useTranslation } from 'react-i18next';
import {
  Package, User, Mail, Phone, Building, MapPin, Box,
  Clock, Eye, CheckCircle2, XCircle, AlertCircle, Download, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { QuoteStatusBadge, PACKAGING_LABELS } from './StatusBadges';

export const QuoteDetailsModal = ({ 
  quote, 
  open, 
  onOpenChange,
  onStatusUpdate,
  onDownload,
  onDelete
}) => {
  const { t } = useTranslation();
  
  if (!quote) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Package className="w-5 h-5 text-gold" />
              Quote {quote.quote_number}
            </span>
            <QuoteStatusBadge status={quote.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Status Actions */}
          <div className="flex items-center gap-2 p-4 bg-steel-50 rounded-lg">
            <span className="text-sm text-steel-600 mr-2">{t('admin.updateStatus')}:</span>
            <Button
              size="sm"
              variant={quote.status === 'pending' ? 'default' : 'outline'}
              onClick={() => onStatusUpdate(quote.id, 'pending')}
              className="text-xs"
            >
              <Clock className="w-3 h-3 mr-1" /> {t('statuses.pending')}
            </Button>
            <Button
              size="sm"
              variant={quote.status === 'reviewed' ? 'default' : 'outline'}
              onClick={() => onStatusUpdate(quote.id, 'reviewed')}
              className="text-xs"
            >
              <Eye className="w-3 h-3 mr-1" /> {t('statuses.reviewing')}
            </Button>
            <Button
              size="sm"
              variant={quote.status === 'approved' ? 'default' : 'outline'}
              onClick={() => onStatusUpdate(quote.id, 'approved')}
              className="text-xs bg-green-600 hover:bg-green-700"
            >
              <CheckCircle2 className="w-3 h-3 mr-1" /> {t('statuses.accepted')}
            </Button>
            <Button
              size="sm"
              variant={quote.status === 'rejected' ? 'default' : 'outline'}
              onClick={() => onStatusUpdate(quote.id, 'rejected')}
              className="text-xs bg-red-600 hover:bg-red-700"
            >
              <XCircle className="w-3 h-3 mr-1" /> {t('statuses.rejected')}
            </Button>
          </div>

          {/* Contact Info */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-navy-800 flex items-center gap-2">
                <User className="w-4 h-4 text-gold" />
                {t('admin.contactInfo')}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-steel-400" />
                  <span className="text-steel-600">{t('admin.name')}:</span>
                  <span className="text-navy-800 font-medium">{quote.contact_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-steel-400" />
                  <span className="text-steel-600">{t('admin.email')}:</span>
                  <a href={`mailto:${quote.contact_email}`} className="text-blue-600 hover:underline">
                    {quote.contact_email}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-steel-400" />
                  <span className="text-steel-600">{t('admin.phone')}:</span>
                  <a href={`tel:${quote.contact_phone}`} className="text-navy-800">
                    {quote.contact_phone}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-steel-400" />
                  <span className="text-steel-600">{t('admin.company')}:</span>
                  <span className="text-navy-800">{quote.company_name || t('common.na')}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-navy-800 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gold" />
                {t('quote.step2.title')}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="p-3 bg-green-50 rounded-lg">
                  <span className="text-green-600 text-xs font-medium">{t('tracking.origin').toUpperCase()}</span>
                  <p className="text-navy-800 font-medium">
                    {quote.origin_city}, {quote.origin_country}
                    {quote.origin_code && <span className="text-gold ml-1">({quote.origin_code})</span>}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <span className="text-blue-600 text-xs font-medium">{t('tracking.destination').toUpperCase()}</span>
                  <p className="text-navy-800 font-medium">
                    {quote.destination_city}, {quote.destination_country}
                    {quote.destination_code && <span className="text-gold ml-1">({quote.destination_code})</span>}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Service & Cargo Details */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-navy-800 flex items-center gap-2">
                <Package className="w-4 h-4 text-gold" />
                {t('quote.step5.serviceDetails')}
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-steel-600">{t('admin.transportMode')}:</span>
                <span className="text-navy-800 capitalize font-medium">{quote.transport_mode}</span>
                <span className="text-steel-600">{t('admin.cargoType')}:</span>
                <span className="text-navy-800 capitalize">{quote.cargo_type}</span>
                <span className="text-steel-600">{t('admin.incoterm')}:</span>
                <span className="text-navy-800">{quote.incoterm || 'N/A'}</span>
                <span className="text-steel-600">{t('admin.pickupDate')}:</span>
                <span className="text-navy-800">{quote.pickup_date || 'TBD'}</span>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-navy-800 flex items-center gap-2">
                <Box className="w-4 h-4 text-gold" />
                {t('quote.step5.cargoDetails')}
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-steel-600">{t('admin.weight')}:</span>
                <span className="text-navy-800 font-medium">{quote.weight_kg} kg</span>
                <span className="text-steel-600">{t('admin.volume')}:</span>
                <span className="text-navy-800">{quote.volume_cbm || 'N/A'} CBM</span>
                <span className="text-steel-600">{t('admin.quantity')}:</span>
                <span className="text-navy-800">{quote.quantity}</span>
              </div>
            </div>
          </div>

          {/* Cargo Description */}
          <div className="space-y-2">
            <h3 className="font-semibold text-navy-800">{t('admin.cargoDescription')}</h3>
            <p className="text-sm text-steel-600 bg-steel-50 p-3 rounded-lg">
              {quote.cargo_description}
            </p>
          </div>

          {/* Packaging Information */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-semibold text-navy-800 flex items-center gap-2">
              <Box className="w-4 h-4 text-gold" />
              {t('quote.step3.packaging.title')}
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-steel-600">{t('admin.cargoPackaged')}:</span>
              <span className={`font-medium ${quote.is_packaged ? 'text-green-600' : 'text-amber-600'}`}>
                {quote.is_packaged ? t('common.yes') : t('common.no')}
              </span>
              {!quote.is_packaged && (
                <>
                  <span className="text-steel-600">{t('admin.needsSpecialPkg')}:</span>
                  <span className={`font-medium ${quote.needs_special_packaging ? 'text-amber-600' : 'text-steel-600'}`}>
                    {quote.needs_special_packaging ? t('common.yes') : t('common.no')}
                  </span>
                </>
              )}
            </div>
            {quote.packaging_types && quote.packaging_types.length > 0 && (
              <div className="mt-2">
                <span className="text-steel-600 text-sm">{t('admin.packagingTypes')}:</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {quote.packaging_types.map(type => (
                    <Badge key={type} className="bg-gold/10 text-navy-800 border-gold/30">
                      {PACKAGING_LABELS[type] || type}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {quote.packaging_notes && (
              <div className="mt-2">
                <span className="text-steel-600 text-sm">{t('admin.packagingNotes')}:</span>
                <p className="text-sm text-navy-800 bg-steel-50 p-3 rounded-lg mt-1">
                  {quote.packaging_notes}
                </p>
              </div>
            )}
          </div>

          {/* Special Requirements */}
          {quote.special_requirements && (
            <div className="space-y-2 border-t pt-4">
              <h3 className="font-semibold text-navy-800 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                {t('admin.specialRequirements')}
              </h3>
              <p className="text-sm text-steel-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                {quote.special_requirements}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between border-t pt-4">
            <div className="text-xs text-steel-400">
              <span>{t('admin.created')}: {new Date(quote.created_at).toLocaleString()}</span>
              <span className="ml-4">ID: {quote.id}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDownload(quote)}
                className="text-xs"
                data-testid="download-quote-btn"
              >
                <Download className="w-3 h-3 mr-1" /> Download
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => onDelete(quote.id)}
                className="text-xs"
                data-testid="delete-quote-btn"
              >
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
